import { NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { getCDNLink } from '@shared/components/image/utils';
import { SvgService } from '@shared/components/svg/services';
import { LogsService } from '@shared/services/logs';
import { EMPTY, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'app-svg',
  imports: [NgStyle, NgTemplateOutlet],
  templateUrl: './svg.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SvgComponent {
  src = input.required<string>();
  height = input<number>();
  width = input<number>();
  inline = input<boolean>(false);
  alt = input<string>();
  hasShadowCta = input<boolean>(false);
  srcCdn = computed(() => getCDNLink(this.src()));
  isFullSize = signal(false);
  svgContent = signal<SafeHtml>(null!);
  readonly isPriority = input(false);
  protected readonly isLoaded = signal(false);

  private readonly aspectRatio = signal<number | null>(null);

  effectiveWidth = computed<number | null>(() => {
    const w = this.width();
    const h = this.height();
    const r = this.aspectRatio();
    if (typeof w === 'number') return w;
    if (typeof h === 'number' && typeof r === 'number') return h * r;
    return null;
  });

  effectiveHeight = computed<number | null>(() => {
    const w = this.width();
    const h = this.height();
    const r = this.aspectRatio();
    if (typeof h === 'number') return h;
    if (typeof w === 'number' && typeof r === 'number') return w / r;
    return null;
  });

  private readonly sanitizer = inject(DomSanitizer);
  private readonly svgService = inject(SvgService);
  private readonly logs = inject(LogsService);

  constructor() {
    effect(() => {
      const isPriority = this.isPriority();
      const inline = this.inline();
      if (isPriority && inline) {
        this.logs.warn('SvgComponent', '⚠️ isPriority input cannot be used with inline input');
      }
    });
    afterNextRender(() => {
      if (!this.height() && !this.width()) {
        this.isFullSize.set(true);
      }
    });

    effect(() => {
      const src = this.srcCdn();
      if (this.inline() && src) {
        this.svgService
          .get(src)
          .pipe(
            take(1),
            tap((raw) => {
              this.svgContent.set(this.sanitizer.bypassSecurityTrustHtml(raw));
              this.aspectRatio.set(this.extractAspectRatio(raw));
              this.isLoaded.set(true);
            }),
            catchError((e: unknown) => {
              this.logs.error('SvgComponent', `🔴 Unable to retrieve the svg`, { src, error: e });
              this.isLoaded.set(true);
              return EMPTY;
            })
          )
          .subscribe();
      } else {
        this.isLoaded.set(true);
      }
    });
  }

  private extractAspectRatio(svg: string): number | null {
    const m = svg.match(
      /<svg[^>]*\bviewBox\s*=\s*["']\s*([+-]?\d*\.?\d+)[\s,]+([+-]?\d*\.?\d+)[\s,]+([+-]?\d*\.?\d+)[\s,]+([+-]?\d*\.?\d+)\s*["']/i
    );
    if (!m) return null;
    const vbW = Number(m[3]);
    const vbH = Number(m[4]);
    return isFinite(vbW) && isFinite(vbH) && vbW > 0 && vbH > 0 ? vbW / vbH : null;
  }
}
